/**
 * Direct Message Dialog
 * Shows list of DM conversations and allows reading/sending messages.
 * Clicking a conversation opens the thread inside the dialog.
 */

import Phaser from 'phaser';
import { MMOStateManager } from '../../managers/MMOStateManager';
import { MMONetworkManager } from '../../managers/MMONetworkManager';
import { EventBus } from '../../utils/EventBus';
import { ServiceLocator, Services } from '../../utils/ServiceLocator';
import {
  createDialogChrome,
  Colors, CSSColors, Fonts, FontSize, Space, Radius,
} from './GameUITheme';
import { I18n } from '../../i18n/I18nService';
import { isMobile } from '../../utils/ResponsiveHelper';

// ── Data interfaces ──────────────────────────────────────────

interface DMConversation {
  userId: string;
  username: string;
  allianceTag?: string;     // e.g. 'WON'
  sameAlliance?: boolean;   // true if in the player's alliance
  lastMessage: string;
  lastMessageTime: number;
  hasUnread: boolean;
  messages: DMMessage[];    // full thread
}

interface DMMessage {
  id: string;
  senderId: string;
  senderName: string;
  text: string;
  timestamp: number;
  isMe: boolean;
}

// ── Component ────────────────────────────────────────────────

export class DirectMessageDialog extends Phaser.GameObjects.Container {
  private dh: number;
  private contentW: number;

  private mmoState: MMOStateManager;
  private mmoNetwork: MMONetworkManager;
  private conversations: DMConversation[] = [];
  private contentContainer: Phaser.GameObjects.Container;
  private dialogTitleText?: Phaser.GameObjects.Text;

  // Conversation view state
  private htmlOverlay?: HTMLDivElement;
  private messageInput?: HTMLInputElement;
  private msgScrollContainer?: Phaser.GameObjects.Container;
  private msgMask?: Phaser.Display.Masks.GeometryMask;
  private msgMaskGfx?: Phaser.GameObjects.Graphics;
  private backHitZone?: Phaser.GameObjects.Zone;

  constructor(scene: Phaser.Scene, mmoState: MMOStateManager) {
    const mobile = isMobile(scene);
    const dw = mobile ? 340 : 652;
    const dh = mobile ? 540 : 520;
    const { width, height } = scene.cameras.main;
    super(scene, (width - dw) / 2, (height - dh) / 2);
    this.dh = dh;
    this.contentW = dw - Space.lg * 2;

    this.mmoState = mmoState;
    this.mmoNetwork = ServiceLocator.get<MMONetworkManager>(Services.MMO_NETWORK);

    const chrome = createDialogChrome(scene, this, dw, dh, {
      title: I18n.t('dialog.direct_messages'),
      onClose: () => this.close(),
    });
    this.dialogTitleText = chrome.titleText;

    this.contentContainer = scene.add.container(Space.lg, 60);
    this.add(this.contentContainer);

    this.loadConversations();
    this.showConversationList();

    // Clear unread indicator on the main screen
    EventBus.emit('mmo-dm-read-all');

    this.setDepth(2000);
    scene.add.existing(this);
  }

  // ═══════════════════════════════════════════
  //  MOCK DATA
  // ═══════════════════════════════════════════

  private loadConversations(): void {
    const now = Date.now();
    const myId = this.mmoState.getUserId() || 'me';
    const myName = 'You';

    // TODO: Replace mock data with real server data
    this.conversations = [
      {
        userId: 'user-001',
        username: 'Voidwalker',
        allianceTag: 'VDX',
        sameAlliance: false,
        lastMessage: 'Stay away from sector 12-7, that asteroid field is ours.',
        lastMessageTime: now - 3 * 60_000,
        hasUnread: true,
        messages: [
          { id: 'm1', senderId: 'user-001', senderName: 'Voidwalker', text: 'Hey, I noticed your scout near 12-7.', timestamp: now - 15 * 60_000, isMe: false },
          { id: 'm2', senderId: myId, senderName: myName, text: 'Just passing through, scouting the area.', timestamp: now - 12 * 60_000, isMe: true },
          { id: 'm3', senderId: 'user-001', senderName: 'Voidwalker', text: 'That sector belongs to VDX. We have 3 cruisers patrolling.', timestamp: now - 8 * 60_000, isMe: false },
          { id: 'm4', senderId: myId, senderName: myName, text: 'Understood. Any chance we can negotiate passage?', timestamp: now - 5 * 60_000, isMe: true },
          { id: 'm5', senderId: 'user-001', senderName: 'Voidwalker', text: 'Stay away from sector 12-7, that asteroid field is ours.', timestamp: now - 3 * 60_000, isMe: false },
        ],
      },
      {
        userId: 'user-002',
        username: 'NovaPilot',
        allianceTag: undefined,
        sameAlliance: false,
        lastMessage: 'Want to form a trade route between our stations? I have a depot at (45, -12).',
        lastMessageTime: now - 47 * 60_000,
        hasUnread: false,
        messages: [
          { id: 'm6', senderId: 'user-002', senderName: 'NovaPilot', text: 'Hey! I see you have a station near sector 40.', timestamp: now - 2 * 3600_000, isMe: false },
          { id: 'm7', senderId: myId, senderName: myName, text: 'Yeah, waypoint at (38, -8). You?', timestamp: now - 90 * 60_000, isMe: true },
          { id: 'm8', senderId: 'user-002', senderName: 'NovaPilot', text: 'Want to form a trade route between our stations? I have a depot at (45, -12).', timestamp: now - 47 * 60_000, isMe: false },
        ],
      },
      {
        userId: 'user-003',
        username: 'IronClaw',
        allianceTag: 'WON',
        sameAlliance: true,
        lastMessage: 'GG on that fight. Your cruiser build is insane.',
        lastMessageTime: now - 4 * 3600_000,
        hasUnread: false,
        messages: [
          { id: 'm9', senderId: myId, senderName: myName, text: 'Nice work flanking that fighter group!', timestamp: now - 5 * 3600_000, isMe: true },
          { id: 'm10', senderId: 'user-003', senderName: 'IronClaw', text: 'Thanks! The afterburner timing was clutch.', timestamp: now - 4.5 * 3600_000, isMe: false },
          { id: 'm11', senderId: 'user-003', senderName: 'IronClaw', text: 'GG on that fight. Your cruiser build is insane.', timestamp: now - 4 * 3600_000, isMe: false },
        ],
      },
      {
        userId: 'user-004',
        username: 'StardustQueen',
        allianceTag: 'SDQ',
        sameAlliance: false,
        lastMessage: 'Our alliance is recruiting — interested?',
        lastMessageTime: now - 2 * 86_400_000,
        hasUnread: true,
        messages: [
          { id: 'm12', senderId: 'user-004', senderName: 'StardustQueen', text: 'Our alliance is recruiting — interested?', timestamp: now - 2 * 86_400_000, isMe: false },
        ],
      },
      {
        userId: 'user-005',
        username: 'Nebula9',
        allianceTag: 'WON',
        sameAlliance: true,
        lastMessage: 'Thanks for the escort, made it back safe.',
        lastMessageTime: now - 5 * 86_400_000,
        hasUnread: false,
        messages: [
          { id: 'm13', senderId: myId, senderName: myName, text: 'I\'ll cover you through the PvP zone. Stay close.', timestamp: now - 5.1 * 86_400_000, isMe: true },
          { id: 'm14', senderId: 'user-005', senderName: 'Nebula9', text: 'Thanks for the escort, made it back safe.', timestamp: now - 5 * 86_400_000, isMe: false },
        ],
      },
    ];
  }

  // ═══════════════════════════════════════════
  //  CONVERSATION LIST VIEW
  // ═══════════════════════════════════════════

  private showConversationList(): void {
    this.cleanupHTML();
    this.cleanupMsgScroll();
    this.cleanupBackHit();

    this.contentContainer.removeAll(true);
    if (this.dialogTitleText) {
      this.dialogTitleText.setText('DIRECT MESSAGES');
      this.dialogTitleText.setColor(CSSColors.textWhite);
      this.dialogTitleText.setFontSize(FontSize.xl);
    }

    let y = 0;

    if (this.conversations.length === 0) {
      const noConvoText = this.scene.add.text(this.contentW / 2, 60, 'No conversations yet', {
        fontSize: FontSize.md,
        color: CSSColors.textMuted,
        fontFamily: Fonts.body,
      });
      noConvoText.setOrigin(0.5, 0);
      this.contentContainer.add(noConvoText);

      const hint = this.scene.add.text(this.contentW / 2, 100, 'To start a conversation, click on a\nplayer\'s ship or station and select "Message"', {
        fontSize: FontSize.sm,
        color: CSSColors.textDim,
        fontFamily: Fonts.body,
        align: 'center',
        lineSpacing: 4,
      });
      hint.setOrigin(0.5, 0);
      this.contentContainer.add(hint);
      return;
    }

    this.conversations.forEach((convo) => {
      const row = this.createConversationRow(0, y, convo);
      this.contentContainer.add(row);
      y += 70;
    });
  }

  private createConversationRow(x: number, y: number, convo: DMConversation): Phaser.GameObjects.Container {
    const row = this.scene.add.container(x, y);

    const bg = this.scene.add.graphics();
    bg.fillStyle(0x161630, 0.6);
    bg.fillRoundedRect(0, 0, this.contentW, 60, Radius.sm);
    bg.lineStyle(1, Colors.cyan, 0.06);
    bg.strokeRoundedRect(0, 0, this.contentW, 60, Radius.sm);
    row.add(bg);

    // ── Name with alliance tag + unread dot ──
    let nameStr = convo.username;
    if (convo.allianceTag) nameStr = `[${convo.allianceTag}] ${convo.username}`;

    const nameColor = convo.sameAlliance ? '#FDB15B' : '#ffffff';
    const nameText = this.scene.add.text(14, 10, nameStr, {
      fontSize: FontSize.md,
      color: nameColor,
      fontFamily: Fonts.heading,
    });
    row.add(nameText);

    // Unread: small red dot after name
    if (convo.hasUnread) {
      const dotX = 14 + nameText.width + 8;
      const dot = this.scene.add.circle(dotX, 17, 4, 0xff4444);
      row.add(dot);
    }

    // Message preview
    const previewMax = 50;
    const preview = convo.lastMessage.length > previewMax
      ? convo.lastMessage.substring(0, previewMax - 3) + '...'
      : convo.lastMessage;
    const messageText = this.scene.add.text(14, 34, preview, {
      fontSize: FontSize.xs,
      color: convo.hasUnread ? CSSColors.textLight : CSSColors.textDim,
      fontFamily: Fonts.body,
    });
    row.add(messageText);

    // Time
    const timeAgo = this.formatTimeAgo(convo.lastMessageTime);
    const timeText = this.scene.add.text(this.contentW - 14, 12, timeAgo, {
      fontSize: FontSize.xs,
      color: CSSColors.textMuted,
      fontFamily: Fonts.body,
    });
    timeText.setOrigin(1, 0);
    row.add(timeText);

    // Hit zone
    const hit = this.scene.add.zone(0, 0, this.contentW, 60).setOrigin(0, 0);
    hit.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.contentW, 60), Phaser.Geom.Rectangle.Contains);
    hit.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(0x1e1e44, 0.8);
      bg.fillRoundedRect(0, 0, this.contentW, 60, Radius.sm);
      bg.lineStyle(1, Colors.cyan, 0.12);
      bg.strokeRoundedRect(0, 0, this.contentW, 60, Radius.sm);
      this.scene.input.setDefaultCursor('pointer');
    });
    hit.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(0x161630, 0.6);
      bg.fillRoundedRect(0, 0, this.contentW, 60, Radius.sm);
      bg.lineStyle(1, Colors.cyan, 0.06);
      bg.strokeRoundedRect(0, 0, this.contentW, 60, Radius.sm);
      this.scene.input.setDefaultCursor('default');
    });
    hit.on('pointerdown', () => this.openConversation(convo));
    row.add(hit);

    return row;
  }

  // ═══════════════════════════════════════════
  //  CONVERSATION THREAD VIEW
  // ═══════════════════════════════════════════

  private openConversation(convo: DMConversation): void {
    this.cleanupHTML();
    this.cleanupMsgScroll();

    convo.hasUnread = false; // Mark as read
    this.contentContainer.removeAll(true);

    // ── Header: Back button replaces the title (sits in title bar area) ──
    // Title text is at y=24 in dialog-local coords; contentContainer starts at y=60.
    // So to place in the title bar: y = 24 - 60 = -36
    if (this.dialogTitleText) this.dialogTitleText.setText('\u25C0  Back');

    // Make title text clickable to go back
    if (this.dialogTitleText && !this.backHitZone) {
      this.backHitZone = this.scene.add.zone(
        this.dialogTitleText.x, this.dialogTitleText.y,
        120, 30,
      ).setOrigin(0, 0.5);
      this.backHitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, 120, 30), Phaser.Geom.Rectangle.Contains);
      this.backHitZone.on('pointerover', () => {
        this.dialogTitleText?.setColor('#FFFFFF');
        this.scene.input.setDefaultCursor('pointer');
      });
      this.backHitZone.on('pointerout', () => {
        this.dialogTitleText?.setColor(CSSColors.textMuted);
        this.scene.input.setDefaultCursor('default');
      });
      this.backHitZone.on('pointerdown', () => this.showConversationList());
      this.add(this.backHitZone);
    }
    if (this.dialogTitleText) {
      this.dialogTitleText.setColor(CSSColors.textMuted);
      this.dialogTitleText.setFontSize(FontSize.sm);
    }

    // ── Name + Block below header line ──
    let displayName = convo.username;
    if (convo.allianceTag) displayName = `[${convo.allianceTag}] ${convo.username}`;

    const nameColor = convo.sameAlliance ? '#FDB15B' : '#E0E8FF';
    const nameText = this.scene.add.text(0, 0, displayName, {
      fontSize: FontSize.lg,
      fontStyle: '900',
      color: nameColor,
      fontFamily: Fonts.heading,
      letterSpacing: 1,
    });
    this.contentContainer.add(nameText);

    // Block button (right-aligned, same line as name)
    const blockText = this.scene.add.text(this.contentW, 3, 'BLOCK', {
      fontSize: '10px',
      fontStyle: '900',
      color: '#884444',
      fontFamily: Fonts.heading,
      letterSpacing: 1,
    });
    blockText.setOrigin(1, 0);
    this.contentContainer.add(blockText);

    const blockHit = this.scene.add.zone(this.contentW - blockText.width - 8, 0, blockText.width + 16, 20).setOrigin(0, 0);
    blockHit.setInteractive(new Phaser.Geom.Rectangle(0, 0, blockText.width + 16, 20), Phaser.Geom.Rectangle.Contains);
    blockHit.on('pointerover', () => { blockText.setColor('#FF4444'); this.scene.input.setDefaultCursor('pointer'); });
    blockHit.on('pointerout', () => { blockText.setColor('#884444'); this.scene.input.setDefaultCursor('default'); });
    blockHit.on('pointerdown', () => this.blockUser(convo));
    this.contentContainer.add(blockHit);

    // ── Message area (scrollable) ──
    const msgAreaY = 28;
    const msgAreaH = this.dh - 60 - msgAreaY - 60; // leave room for input at bottom
    this.renderMessages(convo, msgAreaY, msgAreaH);

    // ── Message input (HTML) ──
    this.createMessageInput(convo, msgAreaY + msgAreaH + 8);
  }

  private blockUser(convo: DMConversation): void {
    // Remove from conversations list
    this.conversations = this.conversations.filter(c => c.userId !== convo.userId);
    // TODO: Send block to server when backend supports it
    // Return to list
    this.showConversationList();
  }

  private renderMessages(convo: DMConversation, areaY: number, areaH: number): void {
    // Create scrollable container
    const scrollContainer = this.scene.add.container(0, areaY);
    this.contentContainer.add(scrollContainer);
    this.msgScrollContainer = scrollContainer;

    // Mask to clip messages to the area
    const maskGfx = this.scene.add.graphics();
    maskGfx.fillStyle(0xffffff);
    maskGfx.fillRect(
      this.x + Space.lg,
      this.y + 60 + areaY,
      this.contentW,
      areaH,
    );
    const mask = new Phaser.Display.Masks.GeometryMask(this.scene, maskGfx);
    scrollContainer.setMask(mask);
    this.msgMask = mask;
    this.msgMaskGfx = maskGfx;

    // Render messages bottom-aligned (newest at bottom)
    let totalH = 0;
    const bubbles: { height: number }[] = [];

    // Measure first
    for (const msg of convo.messages) {
      const h = this.estimateBubbleHeight(msg);
      bubbles.push({ height: h });
      totalH += h;
    }

    // Position from top
    let my = 0;
    convo.messages.forEach((msg, i) => {
      this.renderBubble(scrollContainer, msg, my, convo);
      my += bubbles[i].height;
    });

    // Scroll to bottom if content overflows
    if (totalH > areaH) {
      scrollContainer.y = areaY - (totalH - areaH);
    }

    // Scroll with mouse wheel
    const scrollZone = this.scene.add.zone(
      this.contentW / 2, areaY + areaH / 2, this.contentW, areaH,
    ).setInteractive();
    this.contentContainer.add(scrollZone);

    scrollZone.on('wheel', (_pointer: Phaser.Input.Pointer, _dx: number, _dy: number, dz: number) => {
      if (!this.msgScrollContainer) return;
      const newY = this.msgScrollContainer.y - dz * 0.5;
      const minY = areaY - Math.max(0, totalH - areaH);
      const maxY = areaY;
      this.msgScrollContainer.y = Phaser.Math.Clamp(newY, minY, maxY);
    });
  }

  private estimateBubbleHeight(msg: DMMessage): number {
    const maxW = this.contentW * 0.7;
    // Rough estimate: ~7px per character at 12px font, wrap at maxW
    const charsPerLine = Math.floor(maxW / 7);
    const lines = Math.ceil(msg.text.length / charsPerLine);
    return Math.max(lines * 18, 18) + 30; // text + padding + gap
  }

  private renderBubble(
    container: Phaser.GameObjects.Container,
    msg: DMMessage, y: number,
    convo: DMConversation,
  ): void {
    const maxW = this.contentW * 0.7;
    const padH = 12;
    const padV = 8;

    // Text first to measure
    const text = this.scene.add.text(0, 0, msg.text, {
      fontSize: '12px',
      color: msg.isMe ? '#E0E8FF' : '#FFFFFF',
      fontFamily: Fonts.body,
      wordWrap: { width: maxW - padH * 2 },
      lineSpacing: 3,
    });

    const bubbleW = Math.min(text.width + padH * 2, maxW);
    const bubbleH = text.height + padV * 2;

    // Position: right-aligned for me, left-aligned for them
    const bx = msg.isMe ? this.contentW - bubbleW : 0;

    // Bubble background
    const bg = this.scene.add.graphics();
    if (msg.isMe) {
      bg.fillStyle(0x1a3355, 0.8);
      bg.lineStyle(1, 0x2255aa, 0.3);
    } else {
      const isAlly = convo.sameAlliance;
      bg.fillStyle(isAlly ? 0x332a10 : 0x221133, 0.8);
      bg.lineStyle(1, isAlly ? 0x665520 : 0x443366, 0.3);
    }
    bg.fillRoundedRect(bx, y, bubbleW, bubbleH, 8);
    bg.strokeRoundedRect(bx, y, bubbleW, bubbleH, 8);
    container.add(bg);

    // Text
    text.setPosition(bx + padH, y + padV);
    container.add(text);

    // Timestamp
    const timeStr = this.formatTime(msg.timestamp);
    const tsText = this.scene.add.text(
      msg.isMe ? bx - 4 : bx + bubbleW + 4,
      y + bubbleH - 14,
      timeStr,
      { fontSize: '9px', color: '#556688', fontFamily: Fonts.body },
    );
    tsText.setOrigin(msg.isMe ? 1 : 0, 0);
    container.add(tsText);
  }

  // ── Message input ──────────────────────────────────────────

  private createMessageInput(convo: DMConversation, inputY: number): void {
    // Phaser background
    const inputBg = this.scene.add.graphics();
    inputBg.fillStyle(0x0a1525, 0.8);
    inputBg.fillRoundedRect(0, inputY, this.contentW, 40, Radius.sm);
    inputBg.lineStyle(1.5, 0x1e3a5e, 1);
    inputBg.strokeRoundedRect(0, inputY, this.contentW, 40, Radius.sm);
    this.contentContainer.add(inputBg);

    // HTML overlay
    const canvas = this.scene.game.canvas;
    const canvasRect = canvas.getBoundingClientRect();
    const sx = canvasRect.width / canvas.width;
    const sy = canvasRect.height / canvas.height;

    const overlay = document.createElement('div');
    overlay.style.cssText = `position:fixed; z-index:100000; pointer-events:none;
      left:${canvasRect.left}px; top:${canvasRect.top}px;
      width:${canvasRect.width}px; height:${canvasRect.height}px;`;
    document.body.appendChild(overlay);
    this.htmlOverlay = overlay;

    const absX = (this.x + Space.lg) * sx + canvasRect.left;
    const absY = (this.y + 60 + inputY) * sy + canvasRect.top;
    const fieldW = this.contentW * sx;
    const fieldH = 40 * sy;

    // Send button (inside the input)
    const sendBtnW = 60;
    const sendAbsX = absX + fieldW - sendBtnW * sx - 4;

    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Type a message...';
    input.maxLength = 500;
    input.style.cssText = `
      position:fixed; z-index:100001; pointer-events:auto;
      left:${absX + 4}px; top:${absY + 2}px;
      width:${fieldW - sendBtnW * sx - 16}px; height:${fieldH - 4}px;
      background:transparent; color:#e0e8ff; border:none; outline:none;
      font-family:${Fonts.body}; font-size:${13 * sy}px;
      caret-color:#00ffff; padding:0 8px;
    `;
    input.addEventListener('focus', () => {
      if (this.scene.input.keyboard) this.scene.input.keyboard.enabled = false;
      EventBus.emit('chat-input-focused');
      inputBg.clear();
      inputBg.fillStyle(0x0a1525, 0.8);
      inputBg.fillRoundedRect(0, inputY, this.contentW, 40, Radius.sm);
      inputBg.lineStyle(1.5, 0xFDB15B, 1);
      inputBg.strokeRoundedRect(0, inputY, this.contentW, 40, Radius.sm);
    });
    input.addEventListener('blur', () => {
      if (this.scene.input.keyboard) this.scene.input.keyboard.enabled = true;
      EventBus.emit('chat-input-unfocused');
      inputBg.clear();
      inputBg.fillStyle(0x0a1525, 0.8);
      inputBg.fillRoundedRect(0, inputY, this.contentW, 40, Radius.sm);
      inputBg.lineStyle(1.5, 0x1e3a5e, 1);
      inputBg.strokeRoundedRect(0, inputY, this.contentW, 40, Radius.sm);
    });
    input.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') this.sendMessage(convo);
    });
    document.body.appendChild(input);
    this.messageInput = input;

    // Send button
    const sendBtn = document.createElement('button');
    sendBtn.textContent = 'SEND';
    sendBtn.style.cssText = `
      position:fixed; z-index:100001; pointer-events:auto;
      left:${sendAbsX}px; top:${absY + 4}px;
      width:${sendBtnW * sx}px; height:${fieldH - 8}px;
      background:rgba(0,255,255,0.12); color:#00FFFF; border:1px solid rgba(0,255,255,0.3);
      border-radius:4px; cursor:pointer;
      font-family:${Fonts.heading}; font-size:${11 * sy}px; font-weight:900;
      letter-spacing:2px; transition:background .15s;
    `;
    sendBtn.addEventListener('mouseenter', () => { sendBtn.style.background = 'rgba(0,255,255,0.25)'; });
    sendBtn.addEventListener('mouseleave', () => { sendBtn.style.background = 'rgba(0,255,255,0.12)'; });
    sendBtn.addEventListener('click', () => this.sendMessage(convo));
    document.body.appendChild(sendBtn);

    // Store sendBtn in overlay for cleanup
    (overlay as any)._sendBtn = sendBtn;

    setTimeout(() => input.focus(), 100);
  }

  private sendMessage(convo: DMConversation): void {
    const text = this.messageInput?.value.trim();
    if (!text) return;

    const now = Date.now();
    const newMsg: DMMessage = {
      id: `msg-${now}`,
      senderId: this.mmoState.getUserId() || 'me',
      senderName: 'You',
      text,
      timestamp: now,
      isMe: true,
    };

    convo.messages.push(newMsg);
    convo.lastMessage = text;
    convo.lastMessageTime = now;

    // Send via network
    this.mmoNetwork.sendDirectMessage(convo.userId, text);

    // Clear input and re-render
    if (this.messageInput) this.messageInput.value = '';
    this.openConversation(convo);
  }

  // ═══════════════════════════════════════════
  //  UTILITIES
  // ═══════════════════════════════════════════

  private formatTimeAgo(timestamp: number): string {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  }

  private formatTime(timestamp: number): string {
    const d = new Date(timestamp);
    const h = d.getHours().toString().padStart(2, '0');
    const m = d.getMinutes().toString().padStart(2, '0');
    return `${h}:${m}`;
  }

  // ── Cleanup ────────────────────────────────────────────────

  private cleanupHTML(): void {
    if (this.messageInput) { this.messageInput.remove(); this.messageInput = undefined; }
    if (this.htmlOverlay) {
      const sendBtn = (this.htmlOverlay as any)._sendBtn;
      if (sendBtn) sendBtn.remove();
      this.htmlOverlay.remove();
      this.htmlOverlay = undefined;
    }
  }

  private cleanupBackHit(): void {
    if (this.backHitZone) {
      this.backHitZone.destroy();
      this.backHitZone = undefined;
    }
  }

  private cleanupMsgScroll(): void {
    if (this.msgMask) {
      this.msgScrollContainer?.clearMask(true);
      this.msgMask = undefined;
    }
    if (this.msgMaskGfx) {
      this.msgMaskGfx.destroy();
      this.msgMaskGfx = undefined;
    }
    this.msgScrollContainer = undefined;
  }

  public close(): void {
    this.cleanupHTML();
    this.cleanupMsgScroll();
    this.cleanupBackHit();
    if (this.scene.input.keyboard) this.scene.input.keyboard.enabled = true;
    EventBus.emit('chat-input-unfocused');
    this.scene.input.setDefaultCursor('default');
    this.destroy();
  }
}
